import React, { useState, useEffect } from 'react';
import {
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  CalendarDaysIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface FinancialSummary {
  totalRevenue: number;
  totalExpenses: number;
  profit: number;
  profitMargin: number;
  accountsReceivable: number;
  pendingOrders: number;
}

interface MonthlyData {
  month: string;
  income: number;
  expenses: number;
  profit: number;
}

interface ClientIncome {
  clientId: number;
  'Client.name': string;
  totalAmount: number;
}

interface ExpenseCategory {
  category: string;
  totalAmount: number;
}

const FinancialReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary>({
    totalRevenue: 0,
    totalExpenses: 0,
    profit: 0,
    profitMargin: 0,
    accountsReceivable: 0,
    pendingOrders: 0
  });
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [clientIncomes, setClientIncomes] = useState<ClientIncome[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [period, setPeriod] = useState('year');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Effect to fetch data when period or custom dates change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let startToFetch = startDate;
      let endToFetch = endDate;
      let updateStateDates = false;

      if (period !== 'custom') {
        const today = new Date();
        const calculatedStart = new Date();
        if (period === 'month') {
          calculatedStart.setMonth(today.getMonth() - 1);
        } else if (period === 'quarter') {
          calculatedStart.setMonth(today.getMonth() - 3);
        } else if (period === 'year') {
          calculatedStart.setFullYear(today.getFullYear() - 1);
        }
        const calculatedStartDateStr = calculatedStart.toISOString().split('T')[0];
        const calculatedEndDateStr = today.toISOString().split('T')[0];

        startToFetch = calculatedStartDateStr;
        endToFetch = calculatedEndDateStr;
        if (startDate !== calculatedStartDateStr || endDate !== calculatedEndDateStr) {
          updateStateDates = true;
        }
      }

      if (!startToFetch || !endToFetch) {
        if (period === 'custom') {
          console.log("Custom period selected but dates not set, defaulting to 'year'");
          setPeriod('year'); // Default to year if custom dates are invalid
          setLoading(false);
          return;
        }
        console.error("Invalid date range for fetch:", startToFetch, endToFetch);
        setLoading(false);
        // Clear data if dates are invalid
        setFinancialSummary({ totalRevenue: 0, totalExpenses: 0, profit: 0, profitMargin: 0, accountsReceivable: 0, pendingOrders: 0 });
        setMonthlyData([]);
        setClientIncomes([]);
        setExpenseCategories([]);
        return;
      }

      try {
        const response = await axios.get('/reports/financial', {
          params: {
            startDate: startToFetch,
            endDate: endToFetch
          }
        });
        setFinancialSummary(response.data.summary || { totalRevenue: 0, totalExpenses: 0, profit: 0, profitMargin: 0, accountsReceivable: 0, pendingOrders: 0 });
        setMonthlyData(response.data.monthlyData || []);
        setClientIncomes(response.data.revenueByClient || []);
        setExpenseCategories(response.data.expensesByCategory || []);
        if (updateStateDates) {
          setStartDate(startToFetch);
          setEndDate(endToFetch);
        }
      } catch (error: any) {
        console.error('Error fetching financial report data:', error);
        // Clear data on error
        setFinancialSummary({ totalRevenue: 0, totalExpenses: 0, profit: 0, profitMargin: 0, accountsReceivable: 0, pendingOrders: 0 });
        setMonthlyData([]);
        setClientIncomes([]);
        setExpenseCategories([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [period, period === 'custom' ? startDate : undefined, period === 'custom' ? endDate : undefined]); // Added startDate and endDate dependencies
  
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setStartDate('');
    setEndDate('');
  };
  
  const handleDateRangeSubmit = (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!startDate || !endDate) {
    alert('Пожалуйста, выберите обе даты');
    return;
  }
  
  if (new Date(startDate) > new Date(endDate)) {
    alert('Начальная дата должна быть раньше конечной даты');
    return;
  }
  
  if (period === 'custom') {
    setStartDate(new Date(startDate).toISOString().split('T')[0]);
    setEndDate(new Date(endDate).toISOString().split('T')[0]);
  } else {
    setPeriod('custom');
  }
    // Update state, useEffect will trigger fetch
    // if (startDate && endDate) {
    //   setPeriod('custom' + startDate + endDate);
    // }
  };
  
  // Removed separate fetchFinancialData function

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!startDate || !endDate) {
      alert('Пожалуйста, выберите или примените период для печати отчета.');
      return;
    }
    // Construct the URL for the print page with query parameters
    const printUrl = `/reports/financial/print?startDate=${startDate}&endDate=${endDate}`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Format currency
  const formatCurrency = (amount: number | null | undefined) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '0 ₸';
    }
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Финансовый отчет"
        subtitle={`Финансовые показатели за период: ${startDate ? new Date(startDate).toLocaleDateString('ru-KZ') : '?'} - ${endDate ? new Date(endDate).toLocaleDateString('ru-KZ') : '?'}`}
        actions={
          <button
            onClick={handleOpenPrintView} // Use new handler
            className="btn-secondary flex items-center"
            disabled={!startDate || !endDate} // Disable if dates aren't set
          >
            <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
            Печать
          </button>
        }
      />
      
      {/* Period selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-4">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded ${period === 'month' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('month')}
            >
              Месяц
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'quarter' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('quarter')}
            >
              Квартал
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'year' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('year')}
            >
              Год
            </button>
          </div>
        </div>
        
        <form onSubmit={handleDateRangeSubmit} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Начальная дата</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Конечная дата</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-1" />
            Применить
          </button>
        </form>
      </div>
      
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Доходы</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(financialSummary.totalRevenue)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Расходы</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(financialSummary.totalExpenses)}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <ArrowTrendingDownIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Прибыль</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(financialSummary.profit)}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <ScaleIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Additional metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Неоплаченные суммы</h3>
          </div>
          
          <div className="flex flex-col space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Ожидается оплата</p>
                <p className="text-xl font-medium">{formatCurrency(financialSummary.accountsReceivable)}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <BanknotesIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Ожидающие заказы</p>
                <p className="text-xl font-medium">{financialSummary.pendingOrders}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <DocumentArrowDownIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Сравнение доходов и расходов</h3>
          
          <div className="mb-3">
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Доходы</span>
              <span className="text-sm font-medium text-green-600">{formatCurrency(financialSummary.totalRevenue)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="h-2.5 rounded-full bg-green-600"
                style={{ width: '100%' }}
              ></div>
            </div>
          </div>
          
          <div className="mb-3">
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Расходы</span>
              <span className="text-sm font-medium text-red-600">{formatCurrency(financialSummary.totalExpenses)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="h-2.5 rounded-full bg-red-600"
                style={{ width: `${financialSummary.totalRevenue > 0 ? (financialSummary.totalExpenses / financialSummary.totalRevenue) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Прибыль</span>
              <span className="text-sm font-medium text-blue-600">{formatCurrency(financialSummary.profit)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="h-2.5 rounded-full bg-blue-600"
                style={{ width: `${financialSummary.totalRevenue > 0 ? (financialSummary.profit / financialSummary.totalRevenue) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Monthly charts */}
      {monthlyData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="text-lg font-medium mb-4">Ежемесячная динамика</h3>
          
          <div className="overflow-x-auto">
            <div className="min-w-max">
              <div className="flex h-40 items-end space-x-2">
                {monthlyData.map((data, index) => (
                  <div key={index} className="flex flex-col items-center justify-end">
                    <div className="flex space-x-1">
                      <div 
                        className="w-5 bg-green-500 rounded-t" 
                        style={{ height: `${(data.income / Math.max(...monthlyData.map(d => Math.max(d.income, d.expenses)))) * 120}px` }}
                      ></div>
                      <div 
                        className="w-5 bg-red-500 rounded-t" 
                        style={{ height: `${(data.expenses / Math.max(...monthlyData.map(d => Math.max(d.income, d.expenses)))) * 120}px` }}
                      ></div>
                      <div 
                        className={`w-5 rounded-t ${data.profit >= 0 ? 'bg-blue-500' : 'bg-red-300'}`}
                        style={{ height: `${(Math.abs(data.profit) / Math.max(...monthlyData.map(d => Math.max(d.income, d.expenses, Math.abs(d.profit))))) * 120}px` }}
                      ></div>
                    </div>
                    <div className="text-xs mt-1 -rotate-45 origin-top-left w-16 text-center">
                      {data.month}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center mt-8 space-x-6">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
                  <span className="text-xs">Доходы</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
                  <span className="text-xs">Расходы</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded mr-1"></div>
                  <span className="text-xs">Прибыль</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Client incomes */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Доходы по клиентам</h3>
          
          {clientIncomes.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Клиент</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Сумма дохода</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clientIncomes.map((client) => (
                  <tr key={client.clientId}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{client['Client.name']}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(client.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-center">Нет данных для отображения</p>
          )}
        </div>
        
        {/* Expenses by Category */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Расходы по категориям</h3>
          
          {expenseCategories.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {expenseCategories.map((category) => (
                <li key={category.category} className="py-3 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900">{category.category}</span>
                  <span className="text-sm text-gray-500">{formatCurrency(category.totalAmount)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center">Нет данных для отображения</p>
          )}
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-6">
        {/* Total Income Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Доходы</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(financialSummary.totalRevenue)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        
        {/* Total Expenses Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Расходы</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(financialSummary.totalExpenses)}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <ArrowTrendingDownIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        
        {/* Profit Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Прибыль</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(financialSummary.profit)}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <ScaleIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        {/* Profit Margin Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Рентабельность</p>
              <p className="text-2xl font-bold text-gray-900">
                {(financialSummary.profitMargin ?? 0).toFixed(1)}%
              </p>
            </div>
            <div className="bg-teal-100 p-3 rounded-full">
              <ScaleIcon className="h-6 w-6 text-teal-600" />
            </div>
          </div>
        </div>
        
        {/* Unpaid Amount Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ожидается оплата</p>
              <p className="text-xl font-medium">{formatCurrency(financialSummary.accountsReceivable)}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <BanknotesIcon className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        {/* Pending Orders Card (Optional) */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ожидающие заказы</p>
              <p className="text-xl font-medium">{financialSummary.pendingOrders}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <DocumentArrowDownIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialReport;
